import api from "./api";

export type UserStatus = "active" | "demo" | "inactive";

export type User = {
  id: string;
  email: string;
  full_name: string | null;
  profile_url: string | null;
  date_of_birth: string | null;
  gender: string | null;
  phone: string | null;
  city: string | null;
  locale: string;
  status?: UserStatus;
  email_verified?: boolean;
  demo_started_at?: string | null;
  demo_expires_at?: string | null;
  created_at: string;
  roles?: UserRole[];
  athlete_profiles?: AthleteProfile | null;
  coach_profiles?: CoachProfile | null;
};

export type AthleteProfile = {
  club_id: string;
  team_id: string | null;
  vulnerability_flag: boolean;
  vulnerability_notes: string | null;
  guardian_contact: Record<string, any> | null;
  joined_at: string;
  // Current athlete account plan (free | start | campion | elita), resolved
  // from athlete_subscriptions. Defaults to "free" when no active sub.
  plan_code?: string | null;
  // True when the athlete's account is linked to a real parent account
  // (a parent_athletes row exists).
  has_parent_account?: boolean;
  club?: {
    id: string;
    name: string;
    city: string | null;
    sports: string[] | null;
  } | null;
  team?: {
    id: string;
    name: string;
    sport_type: string | null;
    coach_id: string | null;
    coach: {
      id: string;
      full_name: string | null;
      email: string;
    } | null;
  } | null;
};

export type CoachProfile = {
  club_id: string;
  certifications: Record<string, any> | null;
  created_at: string;
  athlete_count?: number;
  club?: {
    id: string;
    name: string;
    city: string | null;
    sports: string[] | null;
  } | null;
};

export type UserRole = {
  id: string;
  code: string;
  label: string;
  scope_type: "platform" | "club" | "team";
  scope_id: string | null;
};

export type UsersListResponse = {
  total: number;
  page: number;
  pageSize: number;
  data: User[];
};

/**
 * List all users with optional filters
 */
export async function getUsers(params?: {
  q?: string;
  role?: string;
  clubId?: string;
  page?: number;
  pageSize?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.q) queryParams.append("q", params.q);
  if (params?.role) queryParams.append("role", params.role);
  if (params?.clubId) queryParams.append("clubId", params.clubId);
  if (params?.page) queryParams.append("page", String(params.page));
  if (params?.pageSize) queryParams.append("pageSize", String(params.pageSize));

  const qs = queryParams.toString();
  const { data } = await api.get<UsersListResponse>(`/users${qs ? `?${qs}` : ""}`);
  return data;
}

/**
 * Get user details by ID
 */
export async function getUserById(userId: string) {
  const { data } = await api.get<User>(`/users/${userId}`);
  return data;
}

/**
 * Get coaches by club
 */
export async function getCoachesByClub(clubId: string) {
  const { data } = await api.get<Array<{
    id: string;
    full_name: string | null;
    email: string;
    phone: string | null;
  }>>(`/users/coaches/${clubId}`);
  return data;
}

/**
 * Create new user
 */
export async function createUser(payload: {
  email: string;
  password: string;
  full_name?: string;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  city?: string;
  county?: string;
  country?: string;
  locale?: string;
  team_id?: string;
  guardian_contact?: {
    full_name?: string;
    email?: string;
    phone?: string;
    relationship?: string;
  };
  roles?: Array<{
    code: string;
    scope_type: "platform" | "club" | "team";
    scope_id?: string;
  }>;
}) {
  const { data } = await api.post<User>("/users", payload);
  return data;
}

/**
 * Update user profile
 */
export async function updateUser(
  userId: string,
  payload: {
    email?: string;
    full_name?: string;
    profile_url?: string;
    date_of_birth?: string;
    gender?: string;
    phone?: string;
    locale?: string;
  }
) {
  const { data } = await api.patch<User>(`/users/${userId}`, payload);
  return data;
}

/**
 * Admin password reset for any user (called from the user detail edit panel).
 * Backend: PATCH /users/:userId/password — requires the caller to be an admin
 * (platform admin OR club admin in scope).
 */
export async function adminResetUserPassword(userId: string, password: string) {
  const { data } = await api.patch<{ success: true }>(`/users/${userId}/password`, { password });
  return data;
}

/**
 * Confirm a user's email address by hand.
 *
 * The verification link expires after 24 hours. When it does, an account that
 * was created for someone — password set, details filled in — cannot be signed
 * into at all: login answers "verify your email first" and there is no
 * unexpired link left to do it with. This is the way back in.
 */
export async function setUserEmailVerified(userId: string, verified: boolean) {
  const { data } = await api.patch<{ success: true; email_verified: boolean }>(
    `/users/${userId}/email-verification`,
    { verified }
  );
  return data;
}

/** Send a fresh verification link, when the person can still be reached. */
export async function resendUserVerification(userId: string) {
  const { data } = await api.post<{ success: true }>(`/users/${userId}/resend-verification`);
  return data;
}

/**
 * Upload profile image
 */
export async function uploadProfileImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ url: string; thumbnail: string }>("/upload/avatar", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Reset user password (admin only)
 */
export async function resetUserPassword(userId: string, password: string) {
  const { data } = await api.patch(`/users/${userId}/password`, { password });
  return data;
}

/**
 * Delete user (soft delete)
 */
export async function deleteUser(userId: string) {
  const { data } = await api.delete(`/users/${userId}`);
  return data;
}

/**
 * Purge seeded demo athlete accounts (athlete.<team>.<n>@igrowth.demo).
 * Platform admin only. Pass dryRun to preview the count + emails without
 * deleting. Returns { count, deleted, emails? }.
 */
export async function purgeDemoAthletes(dryRun = false) {
  const { data } = await api.post<{ count: number; deleted: number; emails?: string[] }>(
    `/users/demo-athletes/purge${dryRun ? "?dryRun=true" : ""}`
  );
  return data;
}

/**
 * Admin Parents listing: parent accounts with their linked children + club.
 */
export type AdminParent = {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  city: string | null;
  created_at: string;
  children: { id: string; full_name: string | null }[];
  club: { id: string; name: string | null } | null;
};

export async function getAdminParents() {
  const { data } = await api.get<AdminParent[]>("/users/parents");
  return data;
}

/**
 * Returns a small "what would be affected" summary used by the
 * deactivate / delete confirmation chips: how many teams the user is
 * attached to + how many future sessions they coach.
 */
export type AdminImpact = {
  teams_count: number;
  athlete_teams_count: number;
  future_sessions_count: number;
};

export async function getUserAdminImpact(userId: string): Promise<AdminImpact> {
  const { data } = await api.get<AdminImpact>(`/users/${userId}/admin-impact`);
  return data;
}

/**
 * Admin update of a target athlete's profile (team, age_group,
 * vulnerability fields, guardian contact). Used by ClubAthletes
 * row-edit modal.
 */
export async function updateAthleteProfile(
  userId: string,
  payload: Partial<{
    team_id: string | null;
    team_ids: string[];
    age_group: string | null;
    vulnerability_flag: boolean;
    vulnerability_notes: string | null;
    has_guardian: boolean;
    guardian_contact: Record<string, any> | null;
  }>
) {
  const { data } = await api.patch(`/users/${userId}/athlete-profile`, payload);
  return data;
}

/**
 * Assign role to user
 */
export async function assignRole(
  userId: string,
  payload: {
    code: string;
    scope_type: "platform" | "club" | "team";
    scope_id?: string;
  }
) {
  const { data } = await api.post<UserRole>(`/users/${userId}/roles`, payload);
  return data;
}

/**
 * Remove role from user
 */
export async function removeRole(userId: string, userRoleId: string) {
  const { data } = await api.delete(`/users/${userId}/roles/${userRoleId}`);
  return data;
}

export type PointLedgerEntry = {
  id: string;
  action_type: string;
  points: number;
  note?: string | null;
  metadata: Record<string, any>;
  created_at: string;
};

export type PointsHistoryResponse = {
  points: PointLedgerEntry[];
  total: number;
  totalPoints: number;
  limit: number;
  offset: number;
};

/** Manual admin points adjustment (positive awards, negative deducts). */
export async function adjustUserPoints(userId: string, payload: { delta: number; reason: string }) {
  const { data } = await api.post<{ success: boolean; delta: number; reason: string; totalPoints: number }>(
    `/users/${userId}/points/adjust`,
    payload
  );
  return data;
}

export type AttendanceRecord = {
  id: string;
  status: "present" | "late" | "absent" | "excused";
  notes: string | null;
  created_at: string;
  session: {
    id: string;
    title: string;
    type: "sport" | "educational";
    start_at: string;
    end_at: string;
    team: {
      id: string;
      name: string;
    };
  };
};

export type SessionsHistoryResponse = {
  attendance: AttendanceRecord[];
  total: number;
  stats: Array<{ status: string; count: number }>;
  limit: number;
  offset: number;
};

/**
 * Get user's points ledger history
 */
export async function getUserPoints(
  userId: string,
  params?: {
    limit?: number;
    offset?: number;
  }
) {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append("limit", String(params.limit));
  if (params?.offset) queryParams.append("offset", String(params.offset));

  const qs = queryParams.toString();
  const { data } = await api.get<PointsHistoryResponse>(
    `/users/${userId}/points${qs ? `?${qs}` : ""}`
  );
  return data;
}

/**
 * Get user's session attendance history
 */
export async function getUserSessions(
  userId: string,
  params?: {
    limit?: number;
    offset?: number;
  }
) {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append("limit", String(params.limit));
  if (params?.offset) queryParams.append("offset", String(params.offset));

  const qs = queryParams.toString();
  const { data } = await api.get<SessionsHistoryResponse>(
    `/users/${userId}/sessions${qs ? `?${qs}` : ""}`
  );
  return data;
}

// Coach teams and athletes types
export type CoachTeamAthlete = {
  id: string;
  email: string;
  full_name: string | null;
  profile_url: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  joined_team_at: string;
  /** Set while the club has not yet accepted them into the team. */
  pending_membership?: { id: string; requested_at: string } | null;
  vulnerability_flag: boolean;
};

export type CoachTeam = {
  id: string;
  name: string;
  sport_type: string | null;
  age_group: string | null;
  club: {
    id: string;
    name: string;
  } | null;
  created_at: string;
  athletes: CoachTeamAthlete[];
  athlete_count: number;
};

export type CoachTeamsResponse = {
  coach: {
    user_id: string;
    club: {
      id: string;
      name: string;
      city: string | null;
    } | null;
    certifications: Record<string, any> | null;
    created_at: string;
  };
  teams: CoachTeam[];
  total_teams: number;
  total_athletes: number;
};

/**
 * Get coach's teams and athletes
 */
export async function getCoachTeams(userId: string) {
  const { data } = await api.get<CoachTeamsResponse>(`/users/${userId}/coach-teams`);
  return data;
}

/**
 * Update user status (platform admin only)
 * cascade=true will also update all coaches/athletes in the user's clubs
 */
export async function updateUserStatus(
  userId: string,
  status: UserStatus,
  cascade: boolean = true
) {
  const { data } = await api.patch<{
    id: string;
    email: string;
    status: UserStatus;
    demo_started_at: string | null;
    demo_expires_at: string | null;
    cascadeCount: number;
  }>(`/users/${userId}/status`, { status, cascade });
  return data;
}

export type StatusHistoryEntry = {
  id: string;
  old_status: UserStatus | null;
  new_status: UserStatus;
  cascade_count: number;
  reason: string | null;
  created_at: string;
  changed_by: {
    id: string;
    email: string;
    full_name: string | null;
  };
};

/**
 * Get user status change history (platform admin only)
 */
export async function getUserStatusHistory(userId: string) {
  const { data } = await api.get<StatusHistoryEntry[]>(`/users/${userId}/status-history`);
  return data;
}
